import { apiJson } from '../api';

export interface SponsoredPlacement {
  id: string;
  title: string;
  body: string;
  cta_text: string;
  cta_url: string;
  sponsor_name: string;
  domain: string | null;
  category: string | null;
  metadata_text: string | null;
  background_color: string | null;
  accent_color: string | null;
  text_color: string | null;
  border_color: string | null;
  is_active: boolean;
  display_order: number;
  start_date: string | null;
  end_date: string | null;
  clicks_count: number;
  impressions_count: number;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// What /active returns: the render fields only, so the reader never receives a
// sponsor's counters or flight dates.
export type PublicSponsoredPlacement = Pick<
  SponsoredPlacement,
  | 'id'
  | 'title'
  | 'body'
  | 'cta_text'
  | 'cta_url'
  | 'sponsor_name'
  | 'domain'
  | 'category'
  | 'metadata_text'
  | 'background_color'
  | 'accent_color'
  | 'text_color'
  | 'border_color'
>;

export interface SponsoredPlacementInput {
  title: string;
  body: string;
  cta_text: string;
  cta_url: string;
  sponsor_name?: string;
  domain?: string | null;
  category?: string | null;
  metadata_text?: string | null;
  background_color?: string | null;
  accent_color?: string | null;
  text_color?: string | null;
  border_color?: string | null;
  is_active?: boolean;
  display_order?: number;
  start_date?: string | null;
  end_date?: string | null;
}

export async function getActiveSponsoredPlacements(): Promise<PublicSponsoredPlacement[]> {
  // public: true keeps this off fetchWithAuth. Both reader pages mount it for
  // signed-out visitors, and the authenticated path would find no token, call
  // logout(), and redirect the whole page to /login.
  const data = await apiJson<PublicSponsoredPlacement[]>('/api/sponsored-placements/active', { public: true });
  return Array.isArray(data) ? data : [];
}

export async function recordSponsoredPlacementClick(placementId: string): Promise<void> {
  try {
    await apiJson(`/api/sponsored-placements/${encodeURIComponent(placementId)}/click`, {
      method: 'POST',
      public: true,
    });
  } catch {
    // Non-blocking telemetry
  }
}

export async function recordSponsoredPlacementImpression(placementId: string): Promise<void> {
  try {
    await apiJson(`/api/sponsored-placements/${encodeURIComponent(placementId)}/impression`, {
      method: 'POST',
      public: true,
    });
  } catch {
    // Non-blocking telemetry
  }
}

export async function listAdminSponsoredPlacements(): Promise<SponsoredPlacement[]> {
  const data = await apiJson<SponsoredPlacement[]>('/api/admin/sponsored-placements');
  return Array.isArray(data) ? data : [];
}

export async function createSponsoredPlacement(data: SponsoredPlacementInput): Promise<SponsoredPlacement> {
  return apiJson<SponsoredPlacement>('/api/admin/sponsored-placements', {
    method: 'POST',
    json: data,
  });
}

export async function updateSponsoredPlacement(id: string, data: SponsoredPlacementInput): Promise<SponsoredPlacement> {
  return apiJson<SponsoredPlacement>(`/api/admin/sponsored-placements/${encodeURIComponent(id)}`, {
    method: 'PUT',
    json: data,
  });
}

export async function deleteSponsoredPlacement(id: string): Promise<void> {
  await apiJson(`/api/admin/sponsored-placements/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function setSponsoredPlacementActive(id: string, is_active: boolean): Promise<SponsoredPlacement> {
  return apiJson<SponsoredPlacement>(`/api/admin/sponsored-placements/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    json: { is_active },
  });
}

export async function reorderSponsoredPlacements(order_ids: string[]): Promise<void> {
  await apiJson('/api/admin/sponsored-placements/reorder', {
    method: 'PUT',
    json: { order_ids },
  });
}
